import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Page } from 'react-pdf';
import PlaybackPreparingLoader from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';

type MobilePdfPageProps = {
  pageNumber: number;
  pageWidth?: number;
  pageHeight?: number;
  turnoverDegree: number;
  isActive: boolean;
  isFirstPage: boolean;
  pageCanvasRef?: React.RefObject<HTMLCanvasElement>;
  onRegisterPageRoot: (page: number, element: HTMLDivElement | null) => void;
  onFirstPageLoadSuccess: (pdfPage: {
    originalWidth: number;
    originalHeight: number;
  }) => void;
  onPageLoadSuccess?: (pageNumber: number) => void;
  intersectionRoot?: Element | null;
};

function MobilePdfPage({
  pageNumber,
  pageWidth,
  pageHeight,
  turnoverDegree,
  isActive,
  isFirstPage,
  pageCanvasRef,
  onRegisterPageRoot,
  onFirstPageLoadSuccess,
  onPageLoadSuccess,
  intersectionRoot
}: MobilePdfPageProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [isPageInView, setIsPageInView] = useState(pageNumber <= 2);

  useEffect(() => {
    if (isActive) setIsPageInView(true);
  }, [isActive]);

  const shouldRenderPage = isPageInView || isActive;

  const assignRootRef = useCallback(
    (element: HTMLDivElement | null) => {
      rootRef.current = element;
      onRegisterPageRoot(pageNumber, element);
    },
    [onRegisterPageRoot, pageNumber]
  );

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !intersectionRoot) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsPageInView(true);
      },
      { root: intersectionRoot, rootMargin: '400px 0px' }
    );

    observer.observe(root);
    return () => observer.disconnect();
  }, [intersectionRoot]);

  return (
    <div
      ref={assignRootRef}
      className={classNames('media_viewer_pdf-mobile-page', {
        'b-active': isActive
      })}
      style={{ minHeight: pageHeight }}
    >
      <div
        className="media_viewer_pdf-inner"
        style={{
          transform: `rotate(${turnoverDegree}deg)`,
          width: pageWidth,
          height: pageHeight
        }}
      >
        {shouldRenderPage ? (
          <Page
            width={pageWidth}
            height={pageHeight}
            scale={1}
            pageNumber={pageNumber}
            className="media_viewer_pdf-page"
            canvasRef={isActive ? pageCanvasRef : undefined}
            renderAnnotationLayer={false}
            onLoadSuccess={(pdfPage) => {
              onPageLoadSuccess?.(pageNumber);
              if (isFirstPage) onFirstPageLoadSuccess(pdfPage);
            }}
            loading={<PlaybackPreparingLoader />}
          />
        ) : null}
      </div>
    </div>
  );
}

export default memo(MobilePdfPage);
